import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { Reveal, MaskLine } from "../components/Reveal";

const STEPS = [
  {
    n: "1",
    title: "Create",
    copy: "Tap the center thumbprint, Name it, add a cover, add music",
  },
  {
    n: "2",
    title: "Add Media",
    copy: "Attach up to 25 photos and videos",
  },
  {
    n: "3",
    title: "Share",
    copy: "Scan QR code, send a message or add by username.",
  },
];

export default function Playlists() {
  return (
    <>
      <section className="relative overflow-hidden partners-wash" data-testid="playlists-hero">
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-36 sm:pt-44 pb-6 text-center">
          <h1 className="hero-title !text-[clamp(1.9rem,3.8vw,3.1rem)]" data-testid="playlists-title">
            <MaskLine delay={0.1}>
              The Playlist, <span className="text-gradient">Reimagined</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="body-lg mt-5 max-w-xl mx-auto"
            data-testid="playlists-sub"
          >
            Bring your music to life with visuals that tell your story.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 sm:mt-12 -mx-5 sm:-mx-10"
          >
            <video
              src="/brand/3dots-playlist.mp4"
              autoPlay
              muted
              loop
              playsInline
              aria-label="3Dots PlayList — a shared Tap In playlist with photos and tracks"
              className="w-full"
              style={{
                WebkitMaskImage: "url(/brand/3dots-mask.png)",
                maskImage: "url(/brand/3dots-mask.png)",
                WebkitMaskSize: "100% 100%",
                maskSize: "100% 100%",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
              }}
              ref={(el) => {
                if (el && window.matchMedia("(prefers-reduced-motion: reduce)").matches) el.pause();
              }}
              data-testid="playlists-3dots"
            />
          </motion.div>
        </div>
      </section>

      <section className="section !pt-0" data-testid="playlists-how">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal className="text-center">
            <h2 className="section-title" data-testid="playlists-how-title">
              How It <span className="text-gradient">Works</span>
            </h2>
            <p className="body-lg mt-4">Build playlists in three steps.</p>
          </Reveal>
          <div className="relative mt-14">
            <div
              className="hidden sm:block absolute top-[7px] left-[17%] right-[17%] h-[3px] rounded-full bg-gradient-to-r from-[#8b4dff] via-[#7c5cfe] to-[#4d7cff] opacity-70"
              aria-hidden="true"
            >
              <span className="timeline-pulse" />
            </div>
            <div className="grid sm:grid-cols-3 gap-10 sm:gap-8">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.09} className="text-center" testid={`playlists-step-${s.n}`}>
                  <span
                    className="step-node relative z-10 grid place-items-center w-4 h-4 mx-auto rounded-full bg-gradient-to-br from-[#8b4dff] to-[#4d7cff] shadow-[0_0_16px_rgba(140,82,255,0.7)] ring-4 ring-[rgb(var(--c-ink))]"
                    style={{ animationDelay: `${i * 2}s` }}
                  />
                  <span className="block font-display text-5xl font-semibold text-[#DDD6FE] mt-5">{s.n}</span>
                  <h3 className="font-display text-lg font-semibold mt-2">{s.title}</h3>
                  <p className="text-[0.92rem] leading-relaxed text-dusk mt-2 max-w-[260px] mx-auto">{s.copy}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section !pt-8" data-testid="producer-spotlight">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal delay={0.1}>
            <div
              className="relative mt-0 rounded-3xl bg-[rgb(var(--c-panel))] border border-grape/25 shadow-card p-6 sm:p-10 overflow-hidden"
              data-testid="producer-card"
            >
              <div className="glow-blob" style={{ width: 320, height: 320, top: "-15%", left: "-5%" }} aria-hidden="true" />
              <div className="relative grid sm:grid-cols-[minmax(0,420px)_1fr] gap-8 sm:gap-12 items-center">
                <div className="relative">
                  <img
                    src="/brand/focus-portrait.jpg"
                    alt="Focus… — 5x Grammy winning producer"
                    className="w-full rounded-2xl ring-1 ring-grape/25"
                    data-testid="producer-photo"
                  />
                </div>
                <div>
                  <h3 className="font-display text-3xl sm:text-4xl font-semibold" data-testid="producer-name">Focus…</h3>
                  <p className="mt-2 font-semibold text-grape-soft" data-testid="producer-credential">5x Grammy Award Winning Producer</p>
                  <p className="mt-4 text-[0.95rem] leading-relaxed text-dusk">
                    On Tap In, Focus brings his legendary catalog to life, sharing his sounds, his
                    favorite tracks, and creating a space for music discovery and connection with
                    fans, artists, and collaborators everywhere.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section !pt-8 pb-24" data-testid="playlists-cta-section">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="section-title" data-testid="playlists-cta-title">
              Create a <span className="text-gradient">Playlist</span>
            </h2>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/access" data-testid="playlists-download-cta" className="btn-primary">
                <Play size={18} fill="currentColor" /> Download Tap In
              </Link>
              <Link to="/sessions" data-testid="playlists-sessions-cta" className="sessions-pill-ghost !border-[#3B82F6]/40 !text-[#60A5FA]">
                Sessions <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
